import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';

interface Props {
  photos: string[];
  alt?: string;
  /**
   * Resolved category / marketplace cover (see src/lib/coverImage.ts). Rendered
   * as a single full-width hero when the poster uploaded no photos, so a
   * project detail page NEVER appears imageless. Labeled as a category photo so
   * nobody mistakes it for a picture of this specific project.
   */
  fallbackCover?: string;
}

const PhotoGallery: React.FC<Props> = ({ photos, alt, fallbackCover }) => {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<string[]>([]);
  const [coverFailed, setCoverFailed] = useState(false);
  const startX = useRef<number | null>(null);
  const deltaX = useRef(0);

  /**
   * Only real URLs count, so an empty string in the array can't create a blank
   * slide, AND any upload that fails to LOAD at runtime (deleted object, CDN
   * error, offline) is dropped from the carousel. If every upload fails the
   * component degrades to the category cover below instead of leaving the
   * detail page imageless or showing broken-image icons.
   */
  const list = (photos || []).filter(
    (p) => typeof p === 'string' && /^https?:\/\//i.test(p.trim()) && !failed.includes(p),
  );

  const markFailed = (url: string) => {
    setFailed((prev) => (prev.includes(url) ? prev : [...prev, url]));
    setIndex(0);
  };

  // ── No usable uploads: branded category cover instead of an empty page ──
  if (list.length === 0) {
    if (!fallbackCover || coverFailed) return null;
    return (
      <div className="mt-6">
        <div className="relative overflow-hidden rounded-2xl bg-slate-800">
          <img
            src={fallbackCover}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            onError={() => setCoverFailed(true)}
            className="h-64 w-full select-none object-cover object-center sm:h-96"
          />
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-slate-950/75 to-transparent" />
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur">
            <ImageIcon className="h-3 w-3" /> {t('cover.category')}
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-400">{t('cover.categoryNote')}</p>
      </div>
    );
  }


  const total = list.length;
  const go = (next: number) => setIndex(((next % total) + total) % total);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    deltaX.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    deltaX.current = e.touches[0].clientX - startX.current;
  };
  const onTouchEnd = () => {
    if (Math.abs(deltaX.current) > 45) go(index + (deltaX.current < 0 ? 1 : -1));
    startX.current = null;
    deltaX.current = 0;
  };

  return (
    <div className="mt-6">
      <div
        className="relative overflow-hidden rounded-2xl bg-slate-900"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') go(index + 1);
          if (e.key === 'ArrowLeft') go(index - 1);
        }}
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {list.map((p, i) => (
            <img
              key={`slide-${p}-${i}`}
              src={p}
              alt={`${alt || t('gallery.photo')} ${i + 1}`}
              draggable={false}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              onError={() => markFailed(p)}
              className="h-64 w-full shrink-0 select-none object-cover object-center sm:h-96"
            />
          ))}

        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label={t('gallery.prev')}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-900 shadow-md transition-colors hover:bg-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label={t('gallery.next')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-900 shadow-md transition-colors hover:bg-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
              {list.map((p, i) => (
                <button
                  key={`dot-${p}-${i}`}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`${t('gallery.photo')} ${i + 1}`}
                  aria-current={i === index}
                  className={`h-2 rounded-full transition-all ${i === index ? 'w-6 bg-white' : 'w-2 bg-white/50'}`}
                />
              ))}
            </div>

            <span className="absolute right-3 top-3 rounded-full bg-slate-900/70 px-2.5 py-1 text-xs font-bold text-white">
              {index + 1}/{total}
            </span>
          </>
        )}
      </div>

      {total > 1 && (
        <>
          <p className="mt-2 text-center text-xs text-slate-400 sm:hidden">{t('gallery.swipeHint')}</p>
          <div className="mt-3 hidden gap-2 sm:flex">
            {list.map((p, i) => (
              <button
                key={`thumb-${p}-${i}`}
                type="button"
                onClick={() => go(i)}
                aria-label={`${t('gallery.photo')} ${i + 1}`}
                className={`overflow-hidden rounded-lg border-2 transition-colors ${
                  i === index ? 'border-orange-500' : 'border-transparent hover:border-slate-300'
                }`}
              >
                <img src={p} alt="" className="h-16 w-20 object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoGallery;
